import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { HexagonPattern, AngularAccent, HandDrawnUnderline } from './GeometricPatterns';
import { TripCardSkeleton, TripStatsSkeleton } from '../../../components/SkeletonLoader';

const EmptyState = ({ type }) => {
  const navigate = useNavigate();

  // Skeleton loading state
  if (type === 'loading') {
    return (
      <div>
        <TripStatsSkeleton />
        {[0,1,2,3]?.map(i => (
          <div key={i} style={{ marginBottom: '16px' }}>
            <TripCardSkeleton index={i} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden flex flex-col items-center justify-center text-center"
      style={{
        background: 'var(--color-card)',
        border: '2px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: '64px 32px',
        minHeight: '400px',
      }}
    >
      <HexagonPattern className="top-0 left-0" opacity={0.05} />
      <HexagonPattern className="bottom-0 right-0" opacity={0.05} />
      <AngularAccent className="top-4 right-4" width={60} height={60} color="#FF5722" />
      <AngularAccent className="bottom-4 left-4" width={60} height={60} color="#004F98" />

      {/* Custom SVG illustration — empty toll road */}
      <svg
        width="160"
        height="130"
        viewBox="0 0 160 130"
        fill="none"
        style={{ marginBottom: '28px' }}
        aria-hidden="true"
      >
        {/* Road surface */}
        <rect x="50" y="70" width="60" height="50" rx="2" fill="rgba(0,79,152,0.08)" stroke="rgba(0,79,152,0.2)" strokeWidth="1.5"/>
        {/* Road center dashes */}
        <line x1="80" y1="75" x2="80" y2="83" stroke="rgba(0,79,152,0.35)" strokeWidth="2" strokeLinecap="round"/>
        <line x1="80" y1="88" x2="80" y2="96" stroke="rgba(0,79,152,0.35)" strokeWidth="2" strokeLinecap="round"/>
        <line x1="80" y1="101" x2="80" y2="109" stroke="rgba(0,79,152,0.35)" strokeWidth="2" strokeLinecap="round"/>
        {/* Toll gate post left */}
        <line x1="50" y1="40" x2="50" y2="75" stroke="rgba(255,87,34,0.5)" strokeWidth="2.5"/>
        {/* Toll gate post right */}
        <line x1="110" y1="40" x2="110" y2="75" stroke="rgba(255,87,34,0.5)" strokeWidth="2.5"/>
        {/* Toll gate bar */}
        <line x1="50" y1="52" x2="110" y2="52" stroke="rgba(255,87,34,0.7)" strokeWidth="3.5" strokeLinecap="round"/>
        {/* Gate stripes */}
        <line x1="60" y1="52" x2="65" y2="58" stroke="rgba(255,87,34,0.4)" strokeWidth="2"/>
        <line x1="75" y1="52" x2="80" y2="58" stroke="rgba(255,87,34,0.4)" strokeWidth="2"/>
        <line x1="90" y1="52" x2="95" y2="58" stroke="rgba(255,87,34,0.4)" strokeWidth="2"/>
        {/* Gate top circles */}
        <circle cx="50" cy="40" r="5" fill="rgba(255,87,34,0.25)" stroke="rgba(255,87,34,0.6)" strokeWidth="1.5"/>
        <circle cx="110" cy="40" r="5" fill="rgba(255,87,34,0.25)" stroke="rgba(255,87,34,0.6)" strokeWidth="1.5"/>
        {/* Empty road sign */}
        <rect x="68" y="10" width="24" height="20" rx="3" fill="rgba(0,79,152,0.1)" stroke="rgba(0,79,152,0.3)" strokeWidth="1.5"/>
        <line x1="80" y1="30" x2="80" y2="40" stroke="rgba(0,79,152,0.3)" strokeWidth="1.5"/>
        {/* Question mark on sign */}
        <text x="80" y="25" textAnchor="middle" fill="rgba(0,79,152,0.5)" fontSize="12" fontWeight="bold">?</text>
        {/* Geometric accents */}
        <line x1="15" y1="20" x2="35" y2="20" stroke="rgba(255,87,34,0.25)" strokeWidth="1.5"/>
        <line x1="15" y1="20" x2="15" y2="40" stroke="rgba(255,87,34,0.25)" strokeWidth="1.5"/>
        <line x1="125" y1="90" x2="145" y2="90" stroke="rgba(0,79,152,0.2)" strokeWidth="1.5"/>
        <line x1="145" y1="90" x2="145" y2="110" stroke="rgba(0,79,152,0.2)" strokeWidth="1.5"/>
        {/* Dot grid accent */}
        <circle cx="20" cy="90" r="1.5" fill="rgba(0,79,152,0.2)"/>
        <circle cx="28" cy="90" r="1.5" fill="rgba(0,79,152,0.2)"/>
        <circle cx="36" cy="90" r="1.5" fill="rgba(0,79,152,0.2)"/>
        <circle cx="20" cy="98" r="1.5" fill="rgba(0,79,152,0.2)"/>
        <circle cx="28" cy="98" r="1.5" fill="rgba(0,79,152,0.2)"/>
        <circle cx="36" cy="98" r="1.5" fill="rgba(0,79,152,0.2)"/>
      </svg>

      <div style={{ position: 'relative', display: 'inline-block', marginBottom: '12px' }}>
        <h3
          style={{
            fontFamily: 'var(--font-heading)',
            fontWeight: 700,
            fontSize: '22px',
            color: 'var(--color-foreground)',
          }}
        >
          Ingen reisehistorikk ennå
        </h3>
        <HandDrawnUnderline width={220} className="mt-1" />
      </div>

      <p
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: '14px',
          color: 'var(--color-muted-foreground)',
          maxWidth: '320px',
          lineHeight: 1.6,
          marginBottom: '28px',
        }}
      >
        Bruk bomkalkulatoren for å beregne reiser. Historikken din vil vises her automatisk.
      </p>

      <button
        onClick={() => navigate('/toll-calculator')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 24px',
          background: 'var(--color-primary)',
          border: '2px solid var(--color-primary)',
          borderRadius: 'var(--radius-sm)',
          fontFamily: 'var(--font-heading)',
          fontWeight: 600,
          fontSize: '14px',
          color: '#fff',
          cursor: 'pointer',
          transition: 'all 200ms cubic-bezier(0.22,1,0.36,1)',
          boxShadow: '0 4px 16px rgba(0,79,152,0.3)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,79,152,0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,79,152,0.3)';
        }}
        onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.96)'}
        onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; }}
      >
        <Icon name="Calculator" size={16} />
        Gå til bomkalkulator
      </button>
    </div>
  );
};

export default EmptyState;
